import { useEffect, useRef } from "react";
import { API_DATE_FORMAT } from "src/consts/global";

/**
 * Hook that manages loading team data when teamId, dates, or showChanges state changes.
 * Ensures diff data is reloaded when navigating between teams with persisted dates.
 * @param {Function} loadTeamDataById - Function to load team data by ID.
 * @param {string} teamId - The current team ID.
 * @param {Object} state - Org chart state containing viewOptions.
 */
const useLoadTeamDataById = (loadTeamDataById, teamId, state) => {
  const prevFromDate = useRef(state?.viewOptions?.fromDate);
  const prevToDate = useRef(state?.viewOptions?.toDate);
  const prevShowChanges = useRef(state?.viewOptions?.showChanges);
  const prevTeamId = useRef(teamId);

  useEffect(() => {
    loadTeamDataById(teamId, teamId);
    /**
     * HACK: This is a hack to load the team data when the teamId changes between page changes.
     * If I add loadTeamDataById to the dependencies, it will cause an infinite loop.
     * It works like this so don't touch it otherwise it will break the org chart.
     *  */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  useEffect(() => {
    const datesChanged =
      (prevFromDate.current?.format(API_DATE_FORMAT) &&
        prevToDate.current?.format(API_DATE_FORMAT) &&
        prevFromDate.current?.format(API_DATE_FORMAT) !==
          state?.viewOptions?.fromDate?.format(API_DATE_FORMAT)) ||
      prevToDate.current?.format(API_DATE_FORMAT) !==
        state?.viewOptions?.toDate?.format(API_DATE_FORMAT) ||
      (prevFromDate.current?.format(API_DATE_FORMAT) &&
        prevToDate.current?.format(API_DATE_FORMAT) &&
        !state?.viewOptions?.fromDate &&
        !state?.viewOptions?.toDate);

    const showChangesChanged =
      prevShowChanges.current !== state?.viewOptions?.showChanges;

    const teamChangedWithShowChanges =
      prevTeamId.current !== teamId &&
      state?.viewOptions?.showChanges &&
      state?.viewOptions?.fromDate &&
      state?.viewOptions?.toDate;

    if (datesChanged || showChangesChanged || teamChangedWithShowChanges) {
      prevFromDate.current = state?.viewOptions?.fromDate;
      prevToDate.current = state?.viewOptions?.toDate;
      prevShowChanges.current = state?.viewOptions?.showChanges;
      prevTeamId.current = teamId;
      loadTeamDataById(teamId, teamId);
    }
    /**
     * HACK: This is a hack to load the team data when the teamId changes between page changes.
     * If I add loadTeamDataById to the dependencies, it will cause an infinite loop.
     * It works like this so don't touch it otherwise it will break the org chart.
     *  */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state?.viewOptions?.fromDate,
    state?.viewOptions?.toDate,
    state?.viewOptions?.showChanges,
    teamId,
  ]);
};

export default useLoadTeamDataById;
